import { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { BACKEND_OUTAGE_UI_ENABLED, TICKER_PREVIEW_MOCK_ENABLED } from "../lib/featureFlags";
import { getPreviewRates, PREVIEW_MOCK_LABEL } from "../lib/previewRates";
import { get as cacheGet, getLastGood as cacheGetLastGood, set as cacheSet } from "../lib/cacheStore";

/**
 * useCurrencyRates — batch-fetch live FX rates for the Ticker.
 *
 * One canonical call per (base, symbols) pair with client-side 60s
 * caching keyed by the same tuple the server uses. Never hammers the
 * upstream — the server also caches 60s, so worst-case we're 1 call
 * per minute per unique base.
 *
 * @param {string}   base    — 3-letter ISO code (e.g. "USD")
 * @param {string[]} symbols — array of ISO codes; order preserved for row rendering
 * @param {object}   opts    — { enabled: boolean } (defaults to true)
 *
 * Returns:
 *   {
 *     rates:      { EUR: 0.87207, JPY: 161.5, ... },  // empty on error
 *     status:     "idle" | "loading" | "success" | "error",
 *     error:      Error | null,
 *     date:       ISO date string | null,
 *     source:     "frankfurter" | "er-api" | null,
 *     cached:     boolean,          // server-side cache hit flag
 *     refresh:    () => Promise,    // force-refresh (bypasses client cache)
 *     lastFetched:number | null,    // epoch ms of last successful fetch
 *   }
 */

// Client cache lives at module scope so multiple ticker mounts share it.
// Structure: { "USD|EUR,JPY,GBP": { fetchedAt: 1737..., payload: {...} } }
const _clientCache = new Map();
const CLIENT_TTL_MS = 60_000;

function cacheKey(base, symbols) {
  return `${(base || "").toUpperCase()}|${[...symbols].map(s => s.toUpperCase()).sort().join(",")}`;
}

export default function useCurrencyRates(base, symbols, opts = {}) {
  const enabled = opts.enabled !== false;
  const [rates, setRates] = useState({});
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [date, setDate] = useState(null);
  const [source, setSource] = useState(null);
  const [cached, setCached] = useState(false);
  const [lastFetched, setLastFetched] = useState(null);
  const abortRef = useRef(null);

  const doFetch = useCallback(
    async (force = false) => {
      if (!enabled) return;
      const b = (base || "").toUpperCase();
      const syms = Array.isArray(symbols) ? symbols : [];
      if (!b || syms.length === 0) return;

      const key = cacheKey(b, syms);
      if (!force) {
        const hit = _clientCache.get(key);
        if (hit && Date.now() - hit.fetchedAt < CLIENT_TTL_MS) {
          setRates(hit.payload.rates || {});
          setDate(hit.payload.date || null);
          setSource(hit.payload.source || null);
          setCached(true);
          setStatus("success");
          setError(null);
          setLastFetched(hit.fetchedAt);
          return;
        }
        // Persistent cross-session cache. Behind the outage-UI flag so
        // Build 84 runtime behavior is unchanged. When the flag is on
        // and we have a fresh (<60s) persistent hit, use it. Otherwise
        // fall through and fetch — but keep the last-good copy for
        // catch-block fallback.
        if (BACKEND_OUTAGE_UI_ENABLED) {
          const persisted = cacheGet(`fx:${key}`, CLIENT_TTL_MS);
          if (persisted?.isFresh) {
            const p = persisted.payload || {};
            setRates(p.rates || {});
            setDate(p.date || null);
            setSource(p.source || null);
            setCached(true);
            setStatus("success");
            setError(null);
            setLastFetched(persisted.savedAt);
            return;
          }
        }
      }

      // Cancel any in-flight fetch (rapid base changes)
      if (abortRef.current) abortRef.current.cancelled = true;
      const token = { cancelled: false };
      abortRef.current = token;

      setStatus("loading");
      try {
        const resp = await api.get("/currency/rates", {
          params: { base: b, symbols: syms.join(",") },
        });
        if (token.cancelled) return;
        const payload = resp.data || {};
        _clientCache.set(key, { fetchedAt: Date.now(), payload });
        // Persist the last-good response so a subsequent outage can
        // hydrate from disk. Behind the outage-UI flag; no-op when off.
        if (BACKEND_OUTAGE_UI_ENABLED) {
          cacheSet(`fx:${key}`, payload);
        }
        setRates(payload.rates || {});
        setDate(payload.date || null);
        setSource(payload.source || null);
        setCached(Boolean(payload.cached));
        setStatus("success");
        setError(null);
        setLastFetched(Date.now());
      } catch (e) {
        if (token.cancelled) return;
        // AI-7 graceful degradation: before erroring out, try the
        // persistent last-good cache so travel users on a flaky network
        // (or during a backend outage) see the most recent rate
        // instead of blanking. Behind the outage-UI flag.
        if (BACKEND_OUTAGE_UI_ENABLED) {
          const persisted = cacheGetLastGood(`fx:${key}`);
          if (persisted?.payload && Object.keys(persisted.payload.rates || {}).length > 0) {
            setRates(persisted.payload.rates);
            setDate(persisted.payload.date || null);
            setSource(persisted.payload.source || "cache");
            setCached(true);
            setStatus("success");
            setError(null);
            setLastFetched(persisted.savedAt);
            return;
          }
        }
        // Preview-only fallback: if the batch endpoint is unreachable
        // AND `TICKER_PREVIEW_MOCK_ENABLED` is true (dev/preview env),
        // swap in labeled mock data so the UI is still exercisable.
        // Production has this flag off, so we never mislead real users.
        if (TICKER_PREVIEW_MOCK_ENABLED) {
          const mock = getPreviewRates(b, syms);
          if (Object.keys(mock).length > 0) {
            setRates(mock);
            setDate(null);
            setSource(PREVIEW_MOCK_LABEL);
            setCached(false);
            setStatus("success");
            setError(null);
            setLastFetched(Date.now());
            return;
          }
        }
        setError(e);
        setStatus("error");
        // Preserve any previously-loaded rates so the ticker keeps
        // showing last-known values instead of blanking.
      }
    },
    [base, symbols, enabled],
  );

  useEffect(() => {
    doFetch(false);
  }, [base, symbols, enabled, doFetch]);

  const refresh = useCallback(() => doFetch(true), [doFetch]);

  return { rates, status, error, date, source, cached, lastFetched, refresh };
}
